import { useEffect, useState } from 'react';
import {
  verifySessionJwt,
  type SessionClaims,
} from '@/launch/verifySessionJwt';
import { RosterView } from '@/launch/RosterView';
import { RecordMyNameView } from '@/launch/RecordMyNameView';
import { parseView } from '@/launch/views';
import { readSession, stripSessionFromUrl } from '@/launch/session';

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'verified'; token: string; claims: SessionClaims };

function CardShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <main className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="max-w-md w-full rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        {children}
      </div>
    </main>
  );
}

export function LaunchView(): JSX.Element {
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    const session = readSession();
    if (!session) {
      setState({
        status: 'error',
        message: 'Missing session token. Launch from your LMS to continue.',
      });
      return;
    }
    stripSessionFromUrl();

    let cancelled = false;
    verifySessionJwt(session.token)
      .then((claims) => {
        if (!cancelled) setState({ status: 'verified', token: session.token, claims });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ status: 'error', message });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'loading') {
    return (
      <CardShell>
        <p className="text-sm text-gray-600">Verifying session…</p>
      </CardShell>
    );
  }

  if (state.status === 'error') {
    return (
      <CardShell>
        <h1 className="text-lg font-semibold text-gray-900">Launch failed</h1>
        <p className="mt-2 text-sm text-red-600 break-words">{state.message}</p>
      </CardShell>
    );
  }

  const view = parseView(state.claims.view);
  if (view === 'record-my-name') {
    return <RecordMyNameView token={state.token} />;
  }
  return <RosterView token={state.token} />;
}
